import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Loader2,
  MapPin,
  Calendar,
  Smartphone,
  Send,
  ExternalLink,
  type LucideIcon,
} from "lucide-react";
import { useJobInfo, type ProcessingJobList, type FileMetadata, type JobInfo } from "@/lib/api";
import { getProjectName } from "@/lib/projectName";
import {
  CONTAINER_LABELS,
  shortContainer,
  formatClock,
  formatCapture,
  getVerdictStyle,
  getContentTypeMeta,
  getCaptured,
  getDateConfidenceLabel,
  getLocationText,
  getDeviceText,
  getSourceText,
  mapsUrl,
  hasFileIntel,
} from "@/lib/fileIntelligence";

interface ProjectInfoModalProps {
  job: ProcessingJobList;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatDate = (s: string) => {
  try {
    return new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return s;
  }
};

const formatBytes = (bytes: number): string => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

const formatDuration = (seconds: number): string => {
  if (!seconds) return "—";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  }
  return `${mins}m ${secs.toString().padStart(2, "0")}s`;
};

const statusBadge = (status: string): { label: string; cls: string } => {
  switch (status) {
    case "COMPLETED":
      return { label: "Completed", cls: "bg-green-100 text-green-700" };
    case "PROCESSING":
    case "PENDING":
      return { label: "Processing", cls: "bg-amber-100 text-amber-700" };
    case "FAILED":
      return { label: "Failed", cls: "bg-red-100 text-red-700" };
    default:
      return { label: status, cls: "bg-gray-100 text-gray-700" };
  }
};

function FileIntelligenceSection({ fm, info }: { fm: FileMetadata; info: JobInfo }) {
  const vs = getVerdictStyle(fm.verdict);
  const headline = fm.verdict_human || "";
  const captured = getCaptured(fm);
  const conf = getDateConfidenceLabel(fm);
  const device = getDeviceText(fm);
  const source = getSourceText(fm);
  const locationText = getLocationText(fm);
  const url = mapsUrl(fm);

  // Content-type pill: "🎬 Short-form video · 1:10 · 71.6 MB · MOV"
  const { label: ctypeLabel, Icon: ContentIcon } = getContentTypeMeta(fm.audio?.content_type);
  const durStr = formatClock(fm.technical?.duration_s ?? info.duration_seconds);
  const sizeStr =
    fm.technical?.size_mb != null
      ? `${fm.technical.size_mb.toFixed(1)} MB`
      : info.file_size_bytes
        ? formatBytes(info.file_size_bytes)
        : "";
  const contStr = shortContainer(info.format, fm.technical?.container);
  const pillParts = [durStr, sizeStr, contStr].filter(Boolean);

  // Compact analysis row, sourced from JobInfo (not file_metadata).
  const stats: [string, string][] = [];
  if (info.language) stats.push(["Language", info.language]);
  if (info.speaker_count) stats.push(["Speakers", String(info.speaker_count)]);
  if (info.word_count) stats.push(["Words", info.word_count.toLocaleString()]);

  // Technical accordion rows.
  const t = fm.technical;
  const techRows: [string, string][] = [];
  const fname = info.filename || (fm.file ? (fm.file.split("/").pop() ?? "") : "");
  if (fname) techRows.push(["Filename", fname]);
  const codecParts = [
    t?.codec,
    t?.resolution || (t?.width && t?.height ? `${t.width}x${t.height}` : null),
    t?.fps ? `${t.fps}fps` : null,
  ].filter(Boolean) as string[];
  if (codecParts.length) techRows.push(["Codec", codecParts.join(" · ")]);
  if (t?.bitrate_kbps != null) techRows.push(["Bitrate", `${t.bitrate_kbps.toLocaleString()} kbps`]);
  if (fm.audio?.codec_info) techRows.push(["Audio", fm.audio.codec_info]);
  if (t?.container) techRows.push(["Container", CONTAINER_LABELS[t.container] ?? t.container]);
  if (t?.rotation) techRows.push(["Rotation", `${t.rotation}°`]);
  if (t?.n_streams != null) techRows.push(["Streams", String(t.n_streams)]);
  if (fm.extraction_method) techRows.push(["Extraction", fm.extraction_method.replace(/\+/g, " + ")]);

  const hasFacts = Boolean(captured || locationText || device || source);

  return (
    <div className="space-y-3">
      {/* Content-type pill */}
      <div className="flex items-center gap-2 text-sm font-medium">
        <ContentIcon className="w-4 h-4 text-primary shrink-0" />
        <span className="truncate">
          {ctypeLabel}
          {pillParts.length > 0 && (
            <span className="text-muted-foreground font-normal"> · {pillParts.join(" · ")}</span>
          )}
        </span>
      </div>

      {/* Verdict block */}
      {headline && (
        <div
          className="rounded-lg px-3 py-2.5 space-y-1"
          style={{ backgroundColor: vs.bg, color: vs.color }}
        >
          <div className="flex items-center gap-2 text-sm font-medium">
            <vs.Icon className="w-4 h-4 shrink-0" />
            <span>{headline}</span>
          </div>
          {fm.verdict_tip && <p className="text-xs leading-snug opacity-80 pl-6">{fm.verdict_tip}</p>}
        </div>
      )}

      {/* Signal pills */}
      {fm.signals && fm.signals.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {fm.signals.map((s, i) => (
            <span
              key={i}
              className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
            >
              {s}
            </span>
          ))}
        </div>
      )}

      {/* Key facts */}
      {hasFacts && (
        <div className="space-y-1.5 border-t pt-3">
          {captured && (
            <IconRow icon={Calendar} label="Captured">
              {formatCapture(captured)}
              {conf ? <span className="text-muted-foreground"> ({conf})</span> : null}
            </IconRow>
          )}
          {locationText && (
            <IconRow icon={MapPin} label="Location">
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  {locationText}
                  <ExternalLink className="w-3 h-3 shrink-0 opacity-70" />
                </a>
              ) : (
                locationText
              )}
            </IconRow>
          )}
          {device && (
            <IconRow icon={Smartphone} label="Device">
              {device}
            </IconRow>
          )}
          {source && (
            <IconRow icon={Send} label="Source">
              {source}
            </IconRow>
          )}
        </div>
      )}

      {/* Compact analysis */}
      {stats.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 border-t pt-3 text-xs text-muted-foreground">
          {stats.map(([label, value], i) => (
            <span key={label} className="inline-flex items-center gap-1">
              {i > 0 && <span className="text-muted-foreground/40 mr-1">·</span>}
              {label} <span className="text-foreground font-medium">{value}</span>
            </span>
          ))}
        </div>
      )}

      {/* Technical details accordion */}
      {techRows.length > 0 && (
        <details className="border-t pt-3 group">
          <summary className="cursor-pointer select-none text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 list-none [&::-webkit-details-marker]:hidden">
            <span className="transition-transform group-open:rotate-90 text-[10px]">▸</span>
            Technical details
          </summary>
          <div className="mt-2 space-y-1">
            {techRows.map(([label, value]) => (
              <Row key={label} label={label} value={value} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function IconRow({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="text-muted-foreground w-20 shrink-0">{label}</span>
      <span className="text-foreground truncate">{children}</span>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span
        className="text-foreground tabular-nums text-right truncate max-w-[60%]"
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
        {title}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

export function ProjectInfoModal({ job, open, onOpenChange }: ProjectInfoModalProps) {
  const { data: info, isLoading } = useJobInfo(open ? job.id : null);
  const projectName = getProjectName(job);
  const badge = statusBadge(job.status);
  const typeLabel = job.type === "VIDEO" ? "Video" : "Transcript";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">{projectName}</DialogTitle>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground">{formatDate(job.created_at)}</span>
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>
              {badge.label}
            </span>
            <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-muted text-foreground">
              {typeLabel}
            </span>
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : info ? (
          <div className="space-y-5 pt-1">
            {hasFileIntel(info.file_metadata) ? (
              <FileIntelligenceSection fm={info.file_metadata} info={info} />
            ) : (
              <>
                <Section title="Source file">
                  <Row label="Filename" value={info.filename || "—"} />
                  <Row label="Format" value={info.format || "—"} />
                  <Row label="File size" value={formatBytes(info.file_size_bytes)} />
                  <Row label="Duration" value={formatDuration(info.duration_seconds)} />
                </Section>

                <Section title="Analysis">
                  <Row
                    label="Speakers"
                    value={info.speaker_count ? String(info.speaker_count) : "—"}
                  />
                  <Row label="Language" value={info.language || "—"} />
                  <Row
                    label="Word count"
                    value={info.word_count ? info.word_count.toLocaleString() : "—"}
                  />
                </Section>
              </>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4">Could not load project info.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
